import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { TenantRole, type Member } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { updateUserTenantRoleCallable } from '@/lib/callables';

type Props = {
  tenantId: string;
  /** Membro-alvo; `null` fecha o diálogo. */
  member: Member | null;
  onClose: () => void;
};

/**
 * Altera o papel de um membro (`updateUserTenantRole`). O servidor bloqueia
 * rebaixar o último OWNER (`failed-precondition`).
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200.
 *
 * Contrato: `updateUserTenantRole({ tenantId, targetUid, newRole })`; o botão
 * fica desabilitado enquanto o papel escolhido é o atual. `member = null` não
 * renderiza nada.
 *
 * Usado por: `features/members/MembersTab.tsx` e `routes/UsersPage.tsx`.
 *
 * Falhas conhecidas:
 *  - o tratamento de erro procura o código (`'failed-precondition'`) dentro de
 *    `err.message`, mas o `FirebaseError` do SDK de Functions traz em
 *    `message` o texto do servidor (ex.: "Promova outro proprietário antes de rebaixar este.") e o código em `err.code`
 *    (`functions/failed-precondition`). A mensagem específica nunca aparece; o operador vê
 *    sempre a genérica (ver #926, achados).
 */
export function MemberRoleDialog({ tenantId, member, onClose }: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [role, setRole] = useState<TenantRole>(TenantRole.MANAGER);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (member) setRole(member.role);
    setError(null);
  }, [member]);

  if (!member) return null;

  async function submit(): Promise<void> {
    if (!member) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateUserTenantRoleCallable({ tenantId, targetUid: member.uid, newRole: role });
      toast.success(t('adminMembers.toast.roleChanged'));
      onClose();
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setError(
        code.includes('failed-precondition')
          ? t('adminMembers.errors.lastOwner')
          : t('adminMembers.errors.roleFailed'),
      );
      console.error('memberRole submit failed', err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible
      onDismiss={(): void => {
        if (!submitting) onClose();
      }}
      header={t('adminMembers.roleDialog.title', { name: member.displayName })}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              {t('adminMembers.addDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void submit();
              }}
              loading={submitting}
              disabled={role === member.role}
            >
              {t('adminMembers.roleDialog.submit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label={t('adminMembers.addDialog.role')}>
          <RadioGroup
            value={role}
            onChange={({ detail }): void => {
              setRole(detail.value as TenantRole);
            }}
            items={[
              { value: TenantRole.OWNER, label: t('adminMembers.roles.OWNER') },
              { value: TenantRole.MANAGER, label: t('adminMembers.roles.MANAGER') },
              { value: TenantRole.STAFF, label: t('adminMembers.roles.STAFF') },
            ]}
          />
        </FormField>
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
